/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, Word, Excel, PowerPoint */

// Initialize auth manager
let authManager = null;

// Add variables to track uploaded files and image editing mode
let uploadedFile = null;
let capturedImageData = null;
let isImageEditingMode = false;

Office.onReady((info) => {
  if (info.host === Office.HostType.Word || 
      info.host === Office.HostType.Excel || 
      info.host === Office.HostType.PowerPoint) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    
    console.log("🚀 Office add-in initialized");
    
    // Initialize auth manager
    authManager = new OfficeAuthManager();
    console.log("🔐 Auth manager initialized:", authManager);
    
    // Ensure generate button is visible
    const runButton = document.getElementById("run");
    if (runButton) {
      runButton.style.display = "flex";
      runButton.style.visibility = "visible";
      runButton.style.opacity = "1";
      console.log("✅ Generate button made visible");
    }
    
    // Set up event listeners
    document.getElementById("run").onclick = generateImage;
    document.getElementById("get-more-credits").onclick = getMoreCredits;
    document.getElementById("cancel-generation").onclick = cancelGeneration;
    document.getElementById("logoutButton").onclick = handleLogout;

    // New Nano Banana Saifs AI event listeners
    document.getElementById("uploadArea").onclick = (event) => {
      console.log("🖱️ Upload area clicked", event.target);
      // Don't trigger file input if clicking the clear button
      if (event.target.closest('#clearUpload')) {
        console.log("❌ Clear button clicked, not triggering file input");
        return;
      }
      console.log("📁 Triggering file input click");
      const fileInput = document.getElementById("fileInput");
      console.log("📁 File input element:", fileInput);
      if (fileInput) {
        fileInput.click();
        console.log("✅ File input clicked");
      } else {
        console.error("❌ File input element not found");
      }
    };
    document.getElementById("fileInput").onchange = (event) => {
      console.log("📁 File input changed", event);
      handleFileUpload(event);
    };
    document.getElementById("getImageFromDoc").onclick = getImageFromDocument;
    document.getElementById("clearUpload").onclick = clearUploadedImage;

    console.log("✅ Event listeners set up");

    // Initial token check
    console.log("🔍 Starting initial token check");
    checkTokens();
    
    // Check authentication status
    console.log("🔍 Starting initial auth status check");
    checkAuthStatus();

    // Set up interval for token checking every 3 seconds
    const tokenCheckInterval = setInterval(() => {
      console.log("🔄 Periodic token check");
      checkTokens();
    }, 3000);

    // Clean up interval when the page is unloaded
    window.addEventListener('unload', () => {
      if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
      }
    });
    
    console.log("🎉 Office add-in setup complete");
  }
});

// Handle file upload
function handleFileUpload(event) {
  console.log("📁 handleFileUpload called", event);
  const file = event.target.files[0];
  console.log("📁 Selected file:", file);
  
  if (file && file.type.startsWith('image/')) {
    console.log("✅ Valid image file selected:", file.name, file.type);
    
    // Store the uploaded file for later use
    uploadedFile = file;
    capturedImageData = null; // Clear any captured image data
    isImageEditingMode = true;
    
    console.log("🔄 Starting file reader");
    const reader = new FileReader();
    reader.onload = function(e) {
      console.log("✅ File reader loaded successfully");
      
      // Update upload area to show selected file
      const uploadArea = document.getElementById("uploadArea");
      const uploadContent = uploadArea.querySelector(".upload-content");
      const clearButton = document.getElementById("clearUpload");
      
      console.log("🎨 Updating UI elements:", {
        uploadArea: !!uploadArea,
        uploadContent: !!uploadContent,
        clearButton: !!clearButton
      });
      
      if (uploadContent) {
        uploadContent.innerHTML = `
          <img src="${e.target.result}" alt="${file.name}" style="max-width: 100%; max-height: 80px; border-radius: 4px; object-fit: cover;">
          <div class="upload-text" style="margin-top: 8px; font-size: 12px;">${file.name}</div>
        `;
        console.log("✅ Upload content updated with image preview");
      } else {
        console.error("❌ Upload content element not found");
      }
      
      if (uploadArea) {
        uploadArea.style.borderColor = "#0078D4";
        uploadArea.style.background = "#F3F9FF";
        console.log("✅ Upload area styling updated");
      }
      
      // Show clear button
      if (clearButton) {
        clearButton.style.display = "flex";
        console.log("✅ Clear button shown");
      } else {
        console.error("❌ Clear button element not found");
      }
      
      // Update button text to indicate editing mode
      const generateButton = document.getElementById("run");
      const generateButtonLabel = generateButton?.querySelector('.ms-Button-label');
      
      console.log("🔄 Updating generate button:", {
        generateButton: !!generateButton,
        generateButtonLabel: !!generateButtonLabel,
        isAuthenticated: !authManager?.isTokenExpired()
      });
      
      if (generateButtonLabel) {
        if (!authManager || authManager.isTokenExpired()) {
          generateButtonLabel.textContent = 'Sign in to Edit Image';
        } else {
          generateButtonLabel.textContent = 'Edit Image';
        }
        console.log("✅ Generate button text updated:", generateButtonLabel.textContent);
      }
      
      showSuccess(`Image "${file.name}" selected for editing!`);
      console.log("🎉 File upload handling completed successfully");
    };
    
    reader.onerror = function(error) {
      console.error("❌ File reader error:", error);
      showError("Failed to read the selected file. Please try again.");
    };
    
    reader.readAsDataURL(file);
  } else {
    console.error("❌ Invalid file selected:", {
      file: file,
      isImage: file?.type?.startsWith('image/')
    });
    showError("Please select a valid image file.");
  }
}

// Get image from document
async function getImageFromDocument() {
  console.log("🖼️ getImageFromDocument called");
  
  try {
    showLoader("Getting image from document...", false);
    
    // Get the current Office host type directly
    const hostType = Office.context.host;
    console.log("🏢 Office host type:", hostType);
    
    // Call the appropriate function based on the host type
    switch (hostType) {
      case Office.HostType.Word:
        console.log("📝 Calling getImageFromWord");
        await getImageFromWord();
        break;
      case Office.HostType.Excel:
        console.log("📊 Calling getImageFromExcel");
        await getImageFromExcel();
        break;
      case Office.HostType.PowerPoint:
        console.log("📽️ Calling getImageFromPowerPoint");
        await getImageFromPowerPoint();
        break;
      default:
        console.error("❌ Unsupported Office application:", hostType);
        hideLoader();
        showError("Image capture is not supported in this Office application.");
    }
  } catch (error) {
    hideLoader();
    console.error("❌ Error getting image from document:", error);
    showError("Error getting image from document. Please try again.");
  }
}

// Get image from Word document
async function getImageFromWord() {
  console.log("📝 getImageFromWord started");
  
  try {
    await Word.run(async (context) => {
      console.log("🔄 Word.run context established");
      
      // Get the selected range
      const range = context.document.getSelection();
      console.log("📍 Selection range obtained");
      
      // Try to get inline pictures first
      const inlineShapes = range.inlinePictures;
      context.load(inlineShapes, "items");
      console.log("🖼️ Loading inline pictures from selection");
      
      await context.sync();
      console.log("✅ Context sync completed, inline pictures loaded");
      console.log("📊 Number of inline pictures found:", inlineShapes.items.length);
      
      if (inlineShapes.items.length > 0) {
        console.log("✅ Found inline images in selection, processing first image");
        
        // Get the first image in the selection
        const image = inlineShapes.items[0];
        
        // Try loading multiple properties to understand what's available
        console.log("🔄 Loading multiple properties for inline image");
        context.load(image, ["base64ImageSrc", "width", "height", "altTextTitle", "altTextDescription"]);
        
        await context.sync();
        console.log("✅ Inline image properties loaded");
        console.log("📊 Image properties:", {
          base64ImageSrc: image.base64ImageSrc ? `${image.base64ImageSrc.length} chars` : 'null',
          width: image.width,
          height: image.height,
          altTextTitle: image.altTextTitle,
          altTextDescription: image.altTextDescription
        });
        
        if (image.base64ImageSrc) {
          console.log("🎯 Using base64ImageSrc data");
          await processWordImage(image.base64ImageSrc, 'word-inline-image.png');
          return;
        } else {
          console.error("❌ No base64ImageSrc data available for inline image");
          
          // Try alternative approach: get image as base64 using different method
          console.log("🔄 Trying alternative base64 extraction method...");
          try {
            // Try to get the image data using getBase64ImageSrc method if available
            if (typeof image.getBase64ImageSrc === 'function') {
              console.log("🔄 Using getBase64ImageSrc method");
              const base64Result = image.getBase64ImageSrc();
              await context.sync();
              
              if (base64Result && base64Result.value) {
                console.log("✅ Alternative method successful:", base64Result.value.length, "chars");
                await processWordImage(base64Result.value, 'word-inline-image-alt.png');
                return;
              }
            } else {
              console.log("⚠️ getBase64ImageSrc method not available");
            }
            
            // Try using Office.context.document.getSelectedDataAsync as fallback
            console.log("🔄 Trying Office.context.document.getSelectedDataAsync fallback");
            Office.context.document.getSelectedDataAsync(
              Office.CoercionType.Image,
              { imageLeft: 0, imageTop: 0, imageWidth: 500, imageHeight: 500 },
              async (result) => {
                console.log("📋 getSelectedDataAsync result:", {
                  status: result.status,
                  hasValue: !!result.value,
                  valueType: typeof result.value,
                  valueLength: result.value ? result.value.length : 0
                });
                
                if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                  try {
                    let base64Data;
                    if (typeof result.value === 'string') {
                      if (result.value.startsWith('data:image')) {
                        base64Data = result.value.split(',')[1];
                      } else {
                        base64Data = result.value;
                      }
                      
                      console.log("✅ Fallback method successful:", base64Data.length, "chars");
                      await processWordImage(base64Data, 'word-fallback-image.png');
                      return;
                    }
                  } catch (fallbackError) {
                    console.error("❌ Fallback processing error:", fallbackError);
                  }
                }
                
                console.error("❌ All image extraction methods failed");
                hideLoader();
                showError("Unable to extract image data. The image format may not be supported or there may be a Word API limitation.");
              }
            );
            
            // Don't continue to the error below since we're trying the async fallback
            return;
            
          } catch (altError) {
            console.error("❌ Alternative extraction error:", altError);
          }
        }
      } else {
        console.warn("⚠️ No inline pictures found in selection");
        
        // Try to get floating images/shapes
        console.log("🔄 Trying to find floating images/shapes in selection");
        
        // Check if there are any shapes in the selection
        try {
          // Get the paragraph that contains the selection
          const paragraph = range.paragraphs.getFirst();
          context.load(paragraph);
          await context.sync();
          
          // Try to get the entire document's inline pictures and shapes
          const allInlineShapes = context.document.body.inlinePictures;
          context.load(allInlineShapes, "items");
          await context.sync();
          
          console.log("📊 Total inline pictures in document:", allInlineShapes.items.length);
          
          if (allInlineShapes.items.length > 0) {
            console.log("💡 Found images in document. Please try one of these methods:");
            console.log("   1. Click directly on an image to select it");
            console.log("   2. Place your cursor right next to an image");
            console.log("   3. Select text that includes an image");
            
            // Try to automatically extract the first available image
            console.log("🔄 Attempting to automatically extract the first available image...");
            try {
              const firstImage = allInlineShapes.items[0];
              context.load(firstImage, "base64ImageSrc");
              await context.sync();
              
              console.log("✅ First image loaded, checking base64 data");
              console.log("📏 Base64 data length:", firstImage.base64ImageSrc ? firstImage.base64ImageSrc.length : 'null');
              
              if (firstImage.base64ImageSrc) {
                console.log("🎯 Successfully extracted first image automatically");
                await processWordImage(firstImage.base64ImageSrc, 'word-auto-extracted-image.png');
                return;
              } else {
                console.warn("⚠️ First image has no base64ImageSrc data");
              }
            } catch (autoExtractError) {
              console.log("⚠️ Auto-extraction failed:", autoExtractError.message);
            }
            
            // Try alternative approach: select the image programmatically
            console.log("🔄 Trying to select the first image programmatically...");
            try {
              const firstImage = allInlineShapes.items[0];
              
              // Try to select the image first
              firstImage.select();
              await context.sync();
              console.log("✅ Image selected programmatically");
              
              // Now try to get the base64 data
              context.load(firstImage, "base64ImageSrc");
              await context.sync();
              
              console.log("📏 Base64 data length after selection:", firstImage.base64ImageSrc ? firstImage.base64ImageSrc.length : 'null');
              
              if (firstImage.base64ImageSrc) {
                console.log("🎯 Successfully extracted image after programmatic selection");
                await processWordImage(firstImage.base64ImageSrc, 'word-selected-image.png');
                return;
              } else {
                console.warn("⚠️ Still no base64ImageSrc data after selection");
              }
            } catch (selectError) {
              console.log("⚠️ Programmatic selection failed:", selectError.message);
            }
            
            // Try getting the image through the paragraph that contains it
            console.log("🔄 Trying to access image through containing paragraph...");
            try {
              const paragraphs = context.document.body.paragraphs;
              context.load(paragraphs, "items");
              await context.sync();
              
              for (let i = 0; i < paragraphs.items.length; i++) {
                const paragraph = paragraphs.items[i];
                const paragraphImages = paragraph.inlinePictures;
                context.load(paragraphImages, "items");
                await context.sync();
                
                if (paragraphImages.items.length > 0) {
                  console.log(`📄 Found ${paragraphImages.items.length} image(s) in paragraph ${i + 1}`);
                  const paragraphImage = paragraphImages.items[0];
                  context.load(paragraphImage, "base64ImageSrc");
                  await context.sync();
                  
                  console.log("📏 Paragraph image base64 length:", paragraphImage.base64ImageSrc ? paragraphImage.base64ImageSrc.length : 'null');
                  
                  if (paragraphImage.base64ImageSrc) {
                    console.log("🎯 Successfully extracted image from paragraph");
                    await processWordImage(paragraphImage.base64ImageSrc, 'word-paragraph-image.png');
                    return;
                  }
                }
              }
            } catch (paragraphError) {
              console.log("⚠️ Paragraph approach failed:", paragraphError.message);
            }
            
            hideLoader();
            showError("No image found in your selection. Please click directly on an image in the document, then try again.");
            return;
          }
        } catch (shapeError) {
          console.log("⚠️ Could not check for shapes:", shapeError.message);
        }
        
        // If no images found anywhere, provide helpful guidance
        console.log("❌ No images found in selection or document");
        hideLoader();
        showError("No images found. Please:\n1. Insert an image into your Word document\n2. Click on the image to select it\n3. Then click 'Get Image from Document'");
        return;
      }
      
      // If we get here, there was an issue with the inline image
      hideLoader();
      showError("Could not extract image data. Please try selecting the image again.");
      
    });
  } catch (error) {
    hideLoader();
    console.error("❌ Error in getImageFromWord:", {
      error: error,
      message: error.message,
      stack: error.stack
    });
    showError("Failed to get image from Word. Please select an image and try again.");
  }
}

// Helper function to process Word image data
async function processWordImage(base64ImageSrc, fileName) {
  console.log("🎯 Processing Word image data");
  console.log("📏 Base64 data length:", base64ImageSrc.length);
  
  try {
    // Convert base64 to blob and create a File object
    const base64Data = base64ImageSrc;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    
    console.log("🔄 Converting base64 to byte arrays");
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const blob = new Blob(byteArrays, { type: 'image/png' });
    const file = new File([blob], fileName, { type: 'image/png' });
    
    console.log("✅ File object created:", {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    // Set the uploaded file
    uploadedFile = file;
    capturedImageData = null;
    isImageEditingMode = true;
    
    console.log("🔄 Updating global variables:", {
      uploadedFile: !!uploadedFile,
      capturedImageData: !!capturedImageData,
      isImageEditingMode: isImageEditingMode
    });
    
    // Update the upload section UI
    console.log("🎨 Calling updateUploadSectionWithFile");
    updateUploadSectionWithFile(file, `data:image/png;base64,${base64Data}`);
    
    hideLoader();
    showSuccess("Image captured from Word document successfully!");
    console.log("🎉 Word image processing completed successfully");
    
  } catch (error) {
    console.error("❌ Error processing Word image:", error);
    hideLoader();
    showError("Failed to process the captured image. Please try again.");
  }
}

// Get image from Excel document
async function getImageFromExcel() {
  try {
    await Excel.run(async (context) => {
      // Get the active worksheet
      const worksheet = context.workbook.worksheets.getActiveWorksheet();
      
      // Get all shapes (images) in the worksheet
      const shapes = worksheet.shapes;
      context.load(shapes, "items");
      
      await context.sync();
      
      if (shapes.items.length > 0) {
        // Find the first image shape
        let imageShape = null;
        for (let i = 0; i < shapes.items.length; i++) {
          const shape = shapes.items[i];
          context.load(shape, "type");
          await context.sync();
          
          if (shape.type === "Image") {
            imageShape = shape;
            break;
          }
        }
        
        if (imageShape) {
          // Try to get the image data (Note: Excel API has limitations)
          try {
            // Load the image properties
            context.load(imageShape, ["width", "height", "left", "top"]);
            await context.sync();
            
            // Excel doesn't provide direct base64 access, so we'll show a helpful message
            hideLoader();
            showError("Excel doesn't support direct image extraction. Please save the image separately and upload it using the upload button.");
          } catch (imageError) {
            hideLoader();
            showError("Unable to extract image from Excel. Please save the image separately and upload it using the upload button.");
          }
        } else {
          hideLoader();
          showError("No images found in the Excel worksheet.");
        }
      } else {
        hideLoader();
        showError("No shapes found in the Excel worksheet.");
      }
    });
  } catch (error) {
    hideLoader();
    console.error("Error getting image from Excel:", error);
    showError("Failed to get image from Excel. Please try uploading the image using the upload button.");
  }
}

// Get image from PowerPoint document
async function getImageFromPowerPoint() {
  console.log("📽️ getImageFromPowerPoint started");
  
  try {
    // PowerPoint has specific limitations with Office.context.document.getSelectedDataAsync
    // Focus on the PowerPoint API approach first
    if (typeof PowerPoint !== 'undefined' && PowerPoint.run) {
      console.log("🔄 Using PowerPoint API approach");
      
      try {
        await PowerPoint.run(async (context) => {
          console.log("🔄 PowerPoint.run context established");
          
          // Get the current selection
          const selection = context.presentation.getSelectedShapes();
          context.load(selection, "items");
          console.log("📍 Loading selected shapes");
          
          await context.sync();
          console.log("✅ Context sync completed");
          console.log("📊 Number of selected shapes:", selection.items.length);
          
          if (selection.items.length === 0) {
            // Try to get all shapes from the current slide to provide better guidance
            console.log("🔍 No shapes selected, checking current slide for available images");
            
            try {
              // Get the active slide
              const slides = context.presentation.slides;
              context.load(slides, "items");
              await context.sync();
              
              if (slides.items.length === 0) {
                throw new Error('No slides found in the presentation.');
              }
              
              // Get the first slide (or you could try to get the active slide)
              const currentSlide = slides.items[0];
              const shapes = currentSlide.shapes;
              context.load(shapes, "items");
              await context.sync();
              
              console.log("📊 Total shapes on slide:", shapes.items.length);
              
              // Count image shapes
              let imageCount = 0;
              let imageShapes = [];
              
              for (let i = 0; i < shapes.items.length; i++) {
                const shape = shapes.items[i];
                context.load(shape, ["type", "name", "id"]);
                await context.sync();
                
                console.log(`Shape ${i + 1}:`, {
                  type: shape.type,
                  name: shape.name,
                  id: shape.id
                });
                
                // PowerPoint API sometimes returns 'Image' as string, sometimes as enum
                const isImage = shape.type === PowerPoint.ShapeType.picture || 
                               shape.type === 'Image' || 
                               shape.type === 'Picture' ||
                               String(shape.type).toLowerCase() === 'image' ||
                               String(shape.type).toLowerCase() === 'picture';
                
                if (isImage) {
                  imageCount++;
                  imageShapes.push({
                    index: i + 1,
                    name: shape.name,
                    id: shape.id
                  });
                }
              }
              
              console.log("📊 Number of images found on slide:", imageCount);
              console.log("🖼️ Image shapes:", imageShapes);
              
              if (imageCount > 0) {
                const imageList = imageShapes.map(img => `• Image ${img.index}: ${img.name || 'Unnamed'}`).join('\n');
                console.log("💡 Found images but none selected. Trying fallback method before showing guidance.");
                // Instead of immediately throwing an error, try the fallback method first
                throw new Error('FALLBACK_NEEDED_NO_SELECTION');
              } else {
                throw new Error('No images found on this slide. Please insert an image first, then select it and try again.');
              }
            } catch (slideError) {
              console.log("⚠️ Error checking slide content:", slideError.message);
              
              // If this is our fallback signal, re-throw it to be handled by the outer catch
              if (slideError.message === 'FALLBACK_NEEDED_NO_SELECTION') {
                throw slideError;
              }
              
              // Otherwise, throw the generic no selection error
              throw new Error('No shapes selected. Please click directly on an image in your PowerPoint slide to select it, then try again.');
            }
          }
          
          // Get the first selected shape
          const shape = selection.items[0];
          context.load(shape, ["type", "name", "id"]);
          console.log("🔄 Loading shape properties");
          
          await context.sync();
          console.log("✅ Shape properties loaded:", {
            type: shape.type,
            name: shape.name,
            id: shape.id
          });
          
          // Check if the shape is an image
          console.log("🔍 Checking shape type:", {
            shapeType: shape.type,
            shapeTypeString: String(shape.type),
            expectedEnum: PowerPoint.ShapeType.picture,
            expectedEnumString: String(PowerPoint.ShapeType.picture)
          });
          
          // PowerPoint API sometimes returns 'Image' as string, sometimes as enum
          const isImage = shape.type === PowerPoint.ShapeType.picture || 
                         shape.type === 'Image' || 
                         shape.type === 'Picture' ||
                         String(shape.type).toLowerCase() === 'image' ||
                         String(shape.type).toLowerCase() === 'picture';
          
          if (!isImage) {
            throw new Error(`Selected shape "${shape.name || 'Unnamed'}" is not an image (type: ${shape.type}). Please select an image instead.`);
          }
          
          console.log("🖼️ Shape is a picture, but PowerPoint Add-in API doesn't support direct image extraction");
          console.log("🔄 Attempting fallback method using Office.context.document.getSelectedDataAsync");
          
          // Instead of immediately throwing an error, try the fallback method
          throw new Error('FALLBACK_NEEDED');
        });
        
        // If we reach here, the PowerPoint API approach succeeded (which shouldn't happen)
        return;
        
      } catch (apiError) {
        console.log("⚠️ PowerPoint API approach result:", apiError.message);
        
        // If the error is our special fallback signal, try the fallback method
        if (apiError.message === 'FALLBACK_NEEDED' || apiError.message === 'FALLBACK_NEEDED_NO_SELECTION') {
          console.log("🔄 Trying Office.context.document.getSelectedDataAsync fallback for PowerPoint");
          
          return new Promise((resolve, reject) => {
            Office.context.document.getSelectedDataAsync(
              Office.CoercionType.Image,
              { 
                imageLeft: 0, 
                imageTop: 0, 
                imageWidth: 500, 
                imageHeight: 500 
              },
              async (result) => {
                console.log("📋 PowerPoint getSelectedDataAsync result:", {
                  status: result.status,
                  hasValue: !!result.value,
                  valueType: typeof result.value,
                  valueLength: result.value ? result.value.length : 0,
                  error: result.error
                });
                
                if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                  try {
                    let base64Data;
                    if (typeof result.value === 'string') {
                      if (result.value.startsWith('data:image')) {
                        base64Data = result.value.split(',')[1];
                      } else {
                        base64Data = result.value;
                      }
                      
                      console.log("✅ PowerPoint fallback method successful:", base64Data.length, "chars");
                      await processPowerPointImage(base64Data, 'powerpoint-fallback-image.png');
                      resolve();
                      return;
                    }
                  } catch (fallbackError) {
                    console.error("❌ PowerPoint fallback processing error:", fallbackError);
                    reject(fallbackError);
                    return;
                  }
                }
                
                // If fallback also failed, show appropriate message based on the original error
                console.error("❌ PowerPoint fallback method also failed");
                hideLoader();
                
                if (apiError.message === 'FALLBACK_NEEDED_NO_SELECTION') {
                  // Show the selection guidance since no shapes were selected
                  showError(`Found image(s) on this slide but none are selected.

Please click directly on an image to select it, then try "Get Image from Document" again.

Alternative solutions if selection doesn't work:
1. **Use the Upload Button**: Right-click the image → "Save as Picture" → then upload using the upload button above
2. **Copy & Paste**: Copy the image, paste into an image editor, save, then upload`);
                } else {
                  // Show the general API limitation message
                  showError(`PowerPoint Add-in API Limitation: Direct image extraction is not supported by Microsoft's Office Add-in platform.

Alternative solutions:
1. **Use the Upload Button**: Right-click the image in PowerPoint → "Save as Picture" → then upload using the upload button above
2. **Copy & Paste**: Copy the image in PowerPoint, paste into an image editor, save, then upload
3. **Extract from File**: Save PowerPoint as .zip, navigate to ppt/media folder to find original images

This is a known limitation of Office Add-ins, not a bug in Nano Banana Saifs AI.`);
                }
                reject(new Error('PowerPoint image extraction failed'));
              }
            );
          });
        } else {
          // Handle other API errors normally
          hideLoader();
          showError(apiError.message);
          return;
        }
      }
    } else {
      console.log("⚠️ PowerPoint API not available");
      hideLoader();
      showError("PowerPoint API is not available. Please ensure you're using a supported version of PowerPoint and try again.");
      return;
    }
    
  } catch (error) {
    hideLoader();
    console.error("❌ Error in getImageFromPowerPoint:", {
      error: error,
      message: error.message,
      stack: error.stack
    });
    
    // Provide more specific error messages
    let errorMessage = "Failed to get image from PowerPoint.";
    
    if (error.message.includes('Please click directly on') || 
        error.message.includes('No images found') ||
        error.message.includes('Please select an image')) {
      errorMessage = error.message;
    } else if (error.message.includes('not an image')) {
      errorMessage = error.message;
    } else if (error.message.includes('No base64 data') || error.message.includes('unsupported format')) {
      errorMessage = error.message + "\n\nTry saving the image separately and using the upload button instead.";
    } else if (error.message.includes('PowerPoint API is not available')) {
      errorMessage = error.message;
    } else if (error.message.includes('PowerPoint Add-in API does not support') || error.message.includes('PowerPoint Add-in API limitation')) {
      errorMessage = `PowerPoint Add-in API Limitation: Direct image extraction is not supported by Microsoft's Office Add-in platform.

Alternative solutions:
1. **Use the Upload Button**: Right-click the image in PowerPoint → "Save as Picture" → then upload using the upload button above
2. **Copy & Paste**: Copy the image in PowerPoint, paste into an image editor, save, then upload
3. **Extract from File**: Save PowerPoint as .zip, navigate to ppt/media folder to find original images

This is a known limitation of Office Add-ins, not a bug in Nano Banana Saifs AI.`;
    } else {
      errorMessage = "Failed to extract image from PowerPoint. Please try:\n\n1. Click directly on an image to select it\n2. Make sure the image is not grouped with other objects\n3. Try using the upload button as an alternative";
    }
    
    showError(errorMessage);
  }
}

// Helper function to process PowerPoint image data
async function processPowerPointImage(base64Data, fileName) {
  console.log("🎯 Processing PowerPoint image data");
  console.log("📏 Base64 data length:", base64Data.length);
  
  try {
    // Convert base64 to blob and create a File object
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    
    console.log("🔄 Converting base64 to byte arrays");
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const blob = new Blob(byteArrays, { type: 'image/png' });
    const file = new File([blob], fileName, { type: 'image/png' });
    
    console.log("✅ PowerPoint File object created:", {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    // Set the uploaded file
    uploadedFile = file;
    capturedImageData = null;
    isImageEditingMode = true;
    
    console.log("🔄 Updating global variables for PowerPoint:", {
      uploadedFile: !!uploadedFile,
      capturedImageData: !!capturedImageData,
      isImageEditingMode: isImageEditingMode
    });
    
    // Update the upload section UI
    console.log("🎨 Calling updateUploadSectionWithFile for PowerPoint");
    updateUploadSectionWithFile(file, `data:image/png;base64,${base64Data}`);
    
    hideLoader();
    showSuccess("Image captured from PowerPoint successfully!");
    console.log("🎉 PowerPoint image processing completed successfully");
    
  } catch (error) {
    console.error("❌ Error processing PowerPoint image:", error);
    hideLoader();
    showError("Failed to process the captured image. Please try again.");
  }
}

// New helper function to update the upload section UI with captured image
function updateUploadSectionWithFile(file, imageDataUrl) {
  console.log("🎨 Updating upload section with captured file:", {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    hasImageDataUrl: !!imageDataUrl
  });
  
  // Update upload area to show selected file
  const uploadArea = document.getElementById("uploadArea");
  const uploadContent = uploadArea.querySelector(".upload-content");
  const clearButton = document.getElementById("clearUpload");
  
  console.log("🔍 DOM elements check:", {
    uploadArea: !!uploadArea,
    uploadContent: !!uploadContent,
    clearButton: !!clearButton
  });
  
  if (uploadContent) {
    uploadContent.innerHTML = `
      <img src="${imageDataUrl}" alt="${file.name}" style="max-width: 100%; max-height: 80px; border-radius: 4px; object-fit: cover;">
      <div class="upload-text" style="margin-top: 8px; font-size: 12px;">${file.name}</div>
    `;
    console.log("✅ Upload content updated with image preview");
  } else {
    console.error("❌ Upload content element not found!");
  }
  
  if (uploadArea) {
    uploadArea.style.borderColor = "#0078D4";
    uploadArea.style.background = "#F3F9FF";
    console.log("✅ Upload area styling updated");
  } else {
    console.error("❌ Upload area element not found!");
  }
  
  // Show clear button
  if (clearButton) {
    clearButton.style.display = "flex";
    console.log("✅ Clear button shown");
  } else {
    console.error("❌ Clear button element not found!");
  }
  
  // Update button text to indicate editing mode
  const generateButton = document.getElementById("run");
  const generateButtonLabel = generateButton?.querySelector('.ms-Button-label');
  
  console.log("🔄 Updating generate button:", {
    generateButton: !!generateButton,
    generateButtonLabel: !!generateButtonLabel,
    isAuthenticated: !authManager?.isTokenExpired()
  });
  
  if (generateButtonLabel) {
    if (!authManager || authManager.isTokenExpired()) {
      generateButtonLabel.textContent = 'Sign in to Edit Image';
    } else {
      generateButtonLabel.textContent = 'Edit Image';
    }
    console.log("✅ Generate button text updated:", generateButtonLabel.textContent);
  } else {
    console.error("❌ Generate button label not found!");
  }
  
  console.log("🎉 Upload section update completed successfully");
}

// Check authentication status
function checkAuthStatus() {
  console.log("🔍 Checking authentication status");
  if (!authManager) {
    console.error("❌ Auth manager not initialized");
    return;
  }
  
  console.log("🔐 Auth manager available, checking token expiry");
  const isAuthenticated = !authManager.isTokenExpired();
  console.log("🔍 Authentication status:", isAuthenticated);
  
  // Check what's in session storage
  const userId = sessionStorage.getItem('user_id');
  const accessToken = sessionStorage.getItem('access_token');
  console.log("💾 Session storage check:", {
    userId: userId ? `${userId.substring(0, 10)}...` : 'null',
    accessToken: accessToken ? `${accessToken.substring(0, 10)}...` : 'null'
  });
  
  updateAuthUI(isAuthenticated);
}

// Update authentication UI
function updateAuthUI(isAuthenticated) {
  console.log("Updating UI for authentication status:", isAuthenticated);
  const logoutButton = document.getElementById('logoutButton');
  const runButton = document.getElementById('run');
  const getMoreCreditsButton = document.getElementById('get-more-credits');
  const generateButtonLabel = runButton.querySelector('.ms-Button-label');
  const tokenDisplay = document.getElementById("token-display");
  const accountBenefits = document.getElementById("account-benefits");
  const creditsFooter = document.getElementById("credits-footer");
    
  if (isAuthenticated) {
    // User is authenticated - show logout button, hide sign-in elements
    logoutButton.style.display = 'inline-block';
    runButton.style.display = 'flex';
    runButton.style.visibility = 'visible';
    runButton.disabled = false;
    
    // Show the credits footer when authenticated
    if (creditsFooter) {
      creditsFooter.style.display = 'flex';
    }
    
    // Hide the account benefits text when authenticated
    if (accountBenefits) {
      accountBenefits.style.display = 'none';
    }
    
    // Set button text based on whether we're in image editing mode
    if (isImageEditingMode) {
      generateButtonLabel.textContent = 'Edit Image';
    } else {
      generateButtonLabel.textContent = 'Generate';
    }
    
    getMoreCreditsButton.disabled = false;
    // Token display will be updated by checkTokens()
  } else {
    // User is not authenticated - show sign-in elements, hide logout
    logoutButton.style.display = 'none';
    runButton.style.display = 'flex';
    runButton.style.visibility = 'visible';
    runButton.disabled = false;
    
    // Hide the credits footer when not authenticated
    if (creditsFooter) {
      creditsFooter.style.display = 'none';
    }
    
    // Show the account benefits text when not authenticated
    if (accountBenefits) {
      accountBenefits.style.display = 'block';
    }
    
    // Set button text based on whether we're in image editing mode
    if (isImageEditingMode) {
      generateButtonLabel.textContent = 'Sign in to Edit Image';
    } else {
      generateButtonLabel.textContent = 'Sign in to Generate';
    }
    
    // Clear the token display when not authenticated
    if (tokenDisplay) {
      tokenDisplay.textContent = '';
      tokenDisplay.classList.remove("premium");
    }
    
    getMoreCreditsButton.disabled = true;
  }
}

// Add variable to track current authentication process
let currentAuthProcess = null;

// Handle login
async function handleLogin() {
  console.log("Login button clicked");
  try {
    showLoader("Authenticating...", false);
    console.log("Starting authentication process");
    
    // Store the authentication promise
    currentAuthProcess = authManager.authenticate();
    const result = await currentAuthProcess;
    currentAuthProcess = null;
    
    console.log("Authentication result:", result);
    
    if (result.success) {
      console.log("Authentication successful");
      showSuccess("Authentication successful!");
      updateAuthUI(true);
    } else {
      // Provide more specific error messages based on the error type
      let errorMessage = result.error;
      console.error("Authentication failed:", errorMessage);
      
      if (errorMessage.includes("Popup was blocked")) {
        errorMessage = "Your browser blocked the authentication popup. Please follow the instructions in the modal.";
      } else if (errorMessage.includes("Authentication cancelled by user")) {
        errorMessage = "Authentication was cancelled.";
      } else if (errorMessage.includes("Authentication timed out")) {
        errorMessage = "Authentication timed out. Please try again.";
      } else if (errorMessage.includes("State mismatch")) {
        errorMessage = "Security verification failed. Please try again.";
      }
      
      showError(`Authentication failed: ${errorMessage}`);
      updateAuthUI(false);
    }
  } catch (error) {
    console.error("Login error:", error);
    showError(`Error: ${error.message}`);
    updateAuthUI(false);
  } finally {
    // Always hide the loader and reset the current process
    hideLoader();
    currentAuthProcess = null;
  }
}

// Add cancel authentication function
async function cancelAuthentication() {
  console.log("Cancelling authentication process");
  if (authManager) {
    authManager.cancelAuth();
  }
  if (currentAuthProcess) {
    currentAuthProcess = null;
  }
  hideLoader();
  showNotification("Authentication cancelled.", "info");
  updateAuthUI(false);
}

// Add cancel credits dialog function
async function cancelCreditsDialog() {
  console.log("Cancelling credits dialog");
  hideLoader();
  showNotification("Purchase cancelled.", "info");
}

// Update showLoader function to handle authentication cancellation
function showLoader(message = "Generating your image...", isGenerating = true) {
  const loader = document.getElementById("loader");
  const loaderText = loader.querySelector(".loader-text");
  const cancelButton = document.getElementById("cancel-generation");
  const authLoadingContent = document.getElementById("auth-loading-content");
  
  loaderText.textContent = message;
  loader.classList.add("active");
  
  // Show appropriate cancel button text based on the operation
  if (cancelButton) {
    if (isGenerating) {
      cancelButton.querySelector(".ms-Button-label").textContent = "Cancel Generation";
      cancelButton.onclick = cancelGeneration;
    } else if (message.includes("Authenticating")) {
      cancelButton.querySelector(".ms-Button-label").textContent = "Cancel";
      cancelButton.onclick = cancelAuthentication;
    } else if (message.includes("purchase")) {
      cancelButton.querySelector(".ms-Button-label").textContent = "Cancel";
      cancelButton.onclick = cancelCreditsDialog;
    } else {
      cancelButton.querySelector(".ms-Button-label").textContent = "Cancel";
      cancelButton.onclick = cancelCreditsDialog;
    }
  }
  
  // Handle authentication loading experience
  if (!isGenerating && message.includes("Authenticating")) {
    // Show the auth loading content
    authLoadingContent.style.display = "block";
    loaderText.style.display = "none";
    
    // Start the animated loading steps
    startAuthLoadingAnimation();
  } else {
    // Show regular loading content
    authLoadingContent.style.display = "none";
    loaderText.style.display = "block";
  }
}

// Function to animate the authentication loading steps
function startAuthLoadingAnimation() {
  const steps = document.querySelectorAll('.auth-loading-step');
  const dots = document.querySelectorAll('.auth-progress-dot');
  let currentStep = 0;
  
  // Clear any existing interval
  if (window.authLoadingInterval) {
    clearInterval(window.authLoadingInterval);
    window.authLoadingInterval = null; // Ensure it's reset
  }
  
  // Function to update the active step
  function updateActiveStep() {
    // Remove active class from all steps and dots
    steps.forEach(step => step.classList.remove('active'));
    dots.forEach(dot => dot.classList.remove('active'));
    
    // Add active class to current step and dot
    if (currentStep < steps.length) {
      steps[currentStep].classList.add('active');
      dots[currentStep].classList.add('active');
      currentStep++;
      
      // If this was the last step, clear the interval
      if (currentStep >= steps.length) {
        if (window.authLoadingInterval) {
          clearInterval(window.authLoadingInterval);
          window.authLoadingInterval = null;
          console.log("Auth animation finished and stopped.");
        }
      }
    } else {
      // This part should ideally not be reached if interval is cleared correctly
      // but as a fallback, clear interval here too
      if (window.authLoadingInterval) {
        clearInterval(window.authLoadingInterval);
        window.authLoadingInterval = null;
      }
    }
  }
  
  // Set initial active step
  updateActiveStep();
  
  // Set interval to change steps every 10 seconds (for 50 second total auth time)
  window.authLoadingInterval = setInterval(updateActiveStep, 10000);
}

// Update hideLoader function to clean up auth loading animation
function hideLoader() {
  const loader = document.getElementById("loader");
  const authLoadingContent = document.getElementById("auth-loading-content");
  
  loader.classList.remove("active");
  
  // Reset auth loading content
  if (authLoadingContent) {
    authLoadingContent.style.display = "none";
    
    // Reset all steps
    const steps = document.querySelectorAll('.auth-loading-step');
    steps.forEach(step => step.classList.remove('active'));
    steps[0].classList.add('active');
  }
  
  // Clear any existing interval
  if (window.authLoadingInterval) {
    clearInterval(window.authLoadingInterval);
    window.authLoadingInterval = null;
  }
  
  // Reset the cancel button to default state
  const cancelButton = document.getElementById("cancel-generation");
  if (cancelButton) {
    cancelButton.querySelector(".ms-Button-label").textContent = "Cancel Generation";
    cancelButton.onclick = cancelGeneration;
  }
  
  // Reset the current controller when hiding loader
  currentGenerationController = null;
}

// Add variable to track the current generation request
let currentGenerationController = null;

// Add variable to track purchase polling interval
let purchaseCheckInterval = null;

// Add debug function to manually stop purchase check
window.debugStopPurchaseCheck = function() {
  console.log('🛑 Manual debug stop of purchase check called');
  console.log('🔍 Current purchaseCheckInterval state:', purchaseCheckInterval);
  
  if (purchaseCheckInterval) {
    console.log('🗑️ Clearing purchase check interval manually');
    clearInterval(purchaseCheckInterval);
    purchaseCheckInterval = null;
    hideLoader();
    showSuccess("Purchase check stopped manually (debug function)");
    return true;
  } else {
    console.log('❌ No active purchase check interval found');
    showNotification("No active purchase check found", "info");
    return false;
  }
};

// Add debug function to check current state
window.debugPurchaseCheckState = function() {
  console.log('🔍 Purchase check debug state:', {
    purchaseCheckInterval: purchaseCheckInterval,
    intervalIsActive: !!purchaseCheckInterval,
    intervalId: purchaseCheckInterval
  });
  return {
    active: !!purchaseCheckInterval,
    intervalId: purchaseCheckInterval
  };
};

/**
 * Check if a user is premium
 * @param {number} userId - The user ID to check premium status for
 * @returns {Promise<boolean>} Whether the user is premium
 */
async function checkPremiumStatus(userId) {
    try {
        if (!userId) {
            throw new Error('User ID is required');
        }
        
        console.log('🔍 Checking premium status for user:', userId);
        const response = await fetch(`https://multiplewords.in/api/account/user_settings/${userId}`);

        if (!response.ok) {
            console.error('❌ Failed to get premium status:', response.status, response.statusText);
            throw new Error(`Failed to get premium status: ${response.status}`);
        }

        const data = await response.json();
        const userRecord = data?.user_info?.find(user => user.user_id === parseInt(userId));
        const isPremium = userRecord?.is_user_paid || false;

        console.log('✅ Premium status retrieved successfully:', isPremium);
        return isPremium;
    } catch (error) {
        console.error('❌ Error checking premium status:', error);
        throw error;
    }
}

/**
 * Get the premium purchase URL
 * @param {number} userId - The user ID to use in the URL
 * @returns {string} The premium purchase URL
 */
function getPremiumPurchaseUrl(userId) {
    return `https://saifs.ai/canva_pricing/${userId}/16`;
}

// Update checkTokens function
async function checkTokens() {
  const tokenDisplay = document.getElementById("token-display");
  const generateButton = document.getElementById("run");
  const creditsFooter = document.getElementById("credits-footer");

  console.log("🔍 checkTokens called - DOM elements check:", {
    tokenDisplay: !!tokenDisplay,
    generateButton: !!generateButton,
    creditsFooter: !!creditsFooter,
    tokenDisplayElement: tokenDisplay,
    currentTokenText: tokenDisplay?.textContent
  });

  try {
    // Check authentication status first
    if (!authManager || authManager.isTokenExpired()) {
      console.log("User is not authenticated. Hiding credits footer.");
      
      // Hide the entire credits footer when not authenticated
      if (creditsFooter) {
        creditsFooter.style.display = "none";
        console.log("✅ Credits footer hidden for non-authenticated user");
      }
      
      // Clear token display when not authenticated
      if (tokenDisplay) {
        tokenDisplay.textContent = '';
        tokenDisplay.classList.remove("premium");
      }
      
      // Set appropriate button text for non-authenticated users
      if (generateButton) {
        const generateButtonLabel = generateButton.querySelector('.ms-Button-label');
        generateButton.disabled = false;
        generateButton.classList.remove('disabled');
        
        // Set button text based on whether we're in image editing mode
        if (isImageEditingMode) {
          generateButtonLabel.textContent = 'Sign in to Edit Image';
        } else {
          generateButtonLabel.textContent = 'Sign in to Generate';
        }
      }
      return; // Exit the function early
    }

    // Show the credits footer when authenticated
    if (creditsFooter) {
      creditsFooter.style.display = "flex";
      console.log("✅ Credits footer shown for authenticated user");
    }

    // Get user ID from auth manager only if authenticated
    const userId = authManager.getUserId();
    if (!userId) {
      console.warn("Authenticated user has no ID available. Check auth flow.");
      if (tokenDisplay) {
        tokenDisplay.textContent = "Error";
        tokenDisplay.classList.remove("premium");
      }
      if (generateButton) {
        generateButton.disabled = true;
        generateButton.classList.add('disabled');
        generateButton.querySelector(".ms-Button-label").textContent = "Authentication Error";
      }
      return;
    }

    console.log("Checking tokens for authenticated user:", userId);

    // First check premium status
    const isPremium = await checkPremiumStatus(userId);

    const response = await fetch(`https://shorts.multiplewords.in/api/tokens_left/get/${userId}`, {
      method: "GET"
    });

    console.log("Token check API response status:", response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Token check full response:", data);
    console.log("Credits object:", data.credits);

    console.log("Is premium user?", isPremium);

    if (isPremium) {
      // Handle premium user
      console.log("Handling premium user display");
      if (tokenDisplay) {
        console.log("🔄 Setting token display to ∞ for premium user");
        tokenDisplay.textContent = "∞";
        tokenDisplay.classList.add("premium");
        console.log("✅ Token display updated:", {
          textContent: tokenDisplay.textContent,
          classList: tokenDisplay.classList.toString(),
          style: tokenDisplay.style.cssText
        });
      } else {
        console.error("❌ tokenDisplay element not found!");
      }
      
      // Ensure button is enabled for premium users, regardless of token count
      if (generateButton) {
        generateButton.disabled = false;
        generateButton.classList.remove('disabled');
        generateButton.querySelector(".ms-Button-label").textContent = "Generate";
        console.log("✅ Generate button updated for premium user");
      }
    } else {
      // Handle regular user
      const tokenCount = data.credits && typeof data.credits.videos !== 'undefined' ? data.credits.videos : 0;
      console.log("Regular user token count:", tokenCount);
      if (tokenDisplay) {
        tokenDisplay.textContent = tokenCount;
        tokenDisplay.classList.remove("premium");
        console.log("✅ Token display updated for regular user:", tokenCount);
      }

      // Disable/enable generate button based on token availability
      if (tokenCount <= 0) {
        console.log("No tokens available, disabling generate button");
        if (generateButton) {
          generateButton.disabled = true;
          generateButton.classList.add('disabled');
          generateButton.querySelector(".ms-Button-label").textContent = "No Credits Available";
        }
      } else {
        console.log("Tokens available, enabling generate button");
        if (generateButton) {
          generateButton.disabled = false;
          generateButton.classList.remove('disabled');
          generateButton.querySelector(".ms-Button-label").textContent = "Generate";
        }
      }
    }
  } catch (error) {
    console.error("Detailed error in checkTokens:", {
      error: error,
      message: error.message,
      stack: error.stack
    });
    if (tokenDisplay) {
      tokenDisplay.textContent = "Error";
      tokenDisplay.classList.remove("premium");
    }
    // Disable button on error and show appropriate message
    if (generateButton) {
      generateButton.disabled = true;
      generateButton.classList.add('disabled');
      generateButton.querySelector(".ms-Button-label").textContent = "Error Checking Credits";
    }
  }
}

// Add cancel generation function
async function cancelGeneration() {
  if (currentGenerationController) {
    // Abort the current request
    currentGenerationController.abort();
    currentGenerationController = null;
    
    // Hide loader and show notification
    hideLoader();
    showNotification("Image generation cancelled.", "success");
    
    // Re-enable generate button
    const generateButton = document.getElementById("run");
    generateButton.disabled = false;
  }
}

// Update generateImage function to handle login if not authenticated
async function generateImage() {
  try {
    // Check if user is authenticated
    if (authManager.isTokenExpired()) {
      // If not authenticated, handle login first
      showLoader("Authenticating...", false);
      const authResult = await authManager.authenticate();
      
      if (!authResult.success) {
        hideLoader();
        showError(`Authentication failed: ${authResult.error}`);
        return;
      }
      
      // Authentication successful, continue with image generation or editing
      hideLoader();
      showSuccess("Authentication successful!");
      updateAuthUI(true);
    }
    
    // Now proceed with image generation or editing
    const userId = authManager.getUserId();
    
    if (!userId) {
      showError("User ID not available. Please try logging in again.");
      return;
    }
    
    const isPremium = await checkPremiumStatus(userId);
    const promptText = document.querySelector(".input-field").value.trim();
    
    if (!promptText) {
      if (isImageEditingMode) {
        showError("Please enter instructions for editing the image.");
      } else {
        showError("Please enter a prompt for the image generation.");
      }
      return;
    }

    // Check if we're in image editing mode
    if (isImageEditingMode && (uploadedFile || capturedImageData)) {
      await editImage(userId, isPremium, promptText);
    } else {
      // Original image generation logic
      const purposeSelect = document.getElementById("image-purpose-select");

      if (!purposeSelect.value) {
        showError("Please select an image purpose.");
        return;
      }
      
      await generateNewImage(userId, isPremium, promptText, purposeSelect.value);
    }

  } catch (error) {
    console.error("Detailed error in generateImage:", {
      error: error,
      message: error.message,
      stack: error.stack
    });
    hideLoader();
    
    let errorMessage = "An error occurred while processing the image.";
    if (error.name === 'AbortError') {
      errorMessage = "Image processing was cancelled.";
    } else if (error.message.includes("HTTP error")) {
      errorMessage = "Failed to connect to the image processing service. Please try again later.";
    } else if (error.name === "TypeError" && error.message.includes("fetch")) {
      errorMessage = "Network error. Please check your internet connection.";
    }
    
    showError(errorMessage);
    
    // Reset button state
    const generateButton = document.getElementById("run");
    generateButton.disabled = false;
  }
}

// New function to handle image editing
async function editImage(userId, isPremium, promptText) {
  console.log("Starting image editing process");
  
  // Check tokens before editing
  console.log("Checking tokens before editing for user:", userId);
  
  const tokenResponse = await fetch(`https://shorts.multiplewords.in/api/tokens_left/get/${userId}`, {
    method: "GET"
  });

  console.log("Pre-editing token check status:", tokenResponse.status);

  if (!tokenResponse.ok) {
    throw new Error(`HTTP error! status: ${tokenResponse.status}`);
  }

  const tokenData = await tokenResponse.json();
  console.log("Pre-editing token check response:", tokenData);
  
  console.log("User premium status:", isPremium);
  console.log("Available video tokens:", tokenData.credits?.videos);
  
  if (!isPremium && (!tokenData.credits || !tokenData.credits.videos || tokenData.credits.videos < 1)) {
    console.log("Token check failed:", {
      hasCredits: !!tokenData.credits,
      videoTokens: tokenData.credits?.videos
    });
    showError("Insufficient tokens. Please get more credits to continue.");
    return;
  }

  // Show loader and disable generate button
  showLoader("Editing your image...");
  const generateButton = document.getElementById("run");
  generateButton.disabled = true;

  // Create AbortController for the request
  currentGenerationController = new AbortController();

  // Create FormData for image editing
  const editFormData = new FormData();
  editFormData.append('prompt', promptText);
  editFormData.append('user_id', userId);
  editFormData.append('isPro', isPremium ? '1' : '0');

  // Add the image file to the form data
  if (uploadedFile) {
    editFormData.append('file', uploadedFile);
    console.log("Using uploaded file for editing:", uploadedFile.name);
  } else if (capturedImageData) {
    // Convert captured image data to blob and add to form data
    const blob = await convertImageDataToBlob(capturedImageData);
    editFormData.append('file', blob, 'captured_image.png');
    console.log("Using captured image data for editing");
  } else {
    throw new Error("No image available for editing");
  }

  console.log("Sending image editing request:", {
    isPro: isPremium ? '1' : '0',
    user_id: userId,
    prompt: promptText,
    hasFile: !!(uploadedFile || capturedImageData),
    endpoint: "https://shorts.multiplewords.in/mwvideos/api/edit_image_from_text"
  });

  const response = await fetch("https://shorts.multiplewords.in/mwvideos/api/edit_image_from_text", {
    method: "POST",
    body: editFormData,
    signal: currentGenerationController.signal
  });

  console.log("Image editing response status:", response.status);
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log("Image editing full response:", data);

  if (data.status === 1 && data.edited_image_url) {
    console.log("Successfully received edited image URL:", data.edited_image_url);
    // Update loader message
    showLoader("Inserting edited image into document...");
    // Insert the edited image into the document
    await insertImageToDocument(data.edited_image_url);
    hideLoader();
    showSuccess("Image edited and inserted successfully!");
    // Update token count after successful editing
    if (!isPremium) {
      console.log("Updating tokens after editing for non-premium user");
      checkTokens();
    }
  } else {
    hideLoader();
    console.error("Invalid API response:", {
      status: data.status,
      hasEditedImageUrl: !!data.edited_image_url
    });
    const errorMsg = data.msg || data.message || "Failed to edit image. Please try again.";
    showError(errorMsg);
  }

  // Reset button state
  generateButton.disabled = false;
}

// New function to handle original image generation
async function generateNewImage(userId, isPremium, promptText, imagePurpose) {
  console.log("Starting image generation process");
  
  // Check tokens before generating
  console.log("Checking tokens before generation for user:", userId);
  
  const tokenResponse = await fetch(`https://shorts.multiplewords.in/api/tokens_left/get/${userId}`, {
    method: "GET"
  });

  console.log("Pre-generation token check status:", tokenResponse.status);

  if (!tokenResponse.ok) {
    throw new Error(`HTTP error! status: ${tokenResponse.status}`);
  }

  const tokenData = await tokenResponse.json();
  console.log("Pre-generation token check response:", tokenData);
  
  console.log("User premium status:", isPremium);
  console.log("Available video tokens:", tokenData.credits?.videos);
  
  if (!isPremium && (!tokenData.credits || !tokenData.credits.videos || tokenData.credits.videos < 1)) {
    console.log("Token check failed:", {
      hasCredits: !!tokenData.credits,
      videoTokens: tokenData.credits?.videos
    });
    showError("Insufficient tokens. Please get more credits to continue.");
    return;
  }

  // Show loader and disable generate button
  showLoader("Generating your image...");
  const generateButton = document.getElementById("run");
  generateButton.disabled = true;

  // Create AbortController for the request
  currentGenerationController = new AbortController();

  // Create FormData for image generation
  const imageFormData = new FormData();
  imageFormData.append('isPro', isPremium ? '1' : '0');
  imageFormData.append('user_id', userId);
  imageFormData.append('prompt', promptText);
  imageFormData.append('purpose', imagePurpose);

  console.log("Sending image generation request:", {
    isPro: isPremium ? '1' : '0',
    user_id: userId,
    prompt: promptText,
    purpose: imagePurpose,
    endpoint: "https://shorts.multiplewords.in/mwvideos/api/generate_image"
  });

  const response = await fetch("https://shorts.multiplewords.in/mwvideos/api/generate_image", {
    method: "POST",
    body: imageFormData,
    signal: currentGenerationController.signal
  });

  console.log("Image generation response status:", response.status);
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log("Image generation full response:", data);

  if (data.status === 1 && data.generated_image && data.generated_image.image_url) {
    console.log("Successfully received image URL:", data.generated_image.image_url);
    // Update loader message
    showLoader("Inserting image into document...");
    // Insert the generated image into the document
    await insertImageToDocument(data.generated_image.image_url);
    hideLoader();
    showSuccess("Image generated and inserted successfully!");
    // Update token count after successful generation
    if (!isPremium) {
      console.log("Updating tokens after generation for non-premium user");
      checkTokens();
    }
  } else {
    hideLoader();
    console.error("Invalid API response:", {
      status: data.status,
      hasGeneratedImage: !!data.generated_image,
      hasImageUrl: data.generated_image?.image_url
    });
    const errorMsg = data.msg || data.message || "Failed to generate image. Please try again.";
    showError(errorMsg);
  }

  // Reset button state
  generateButton.disabled = false;
}

// Helper function to convert captured image data to blob
async function convertImageDataToBlob(imageData) {
  try {
    // If imageData is already a base64 string, convert it to blob
    if (typeof imageData === 'string') {
      const base64Data = imageData.includes(',') ? imageData.split(',')[1] : imageData;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'image/png' });
    }
    
    // If imageData is already a blob or file, return as is
    if (imageData instanceof Blob) {
      return imageData;
    }
    
    // If imageData is an ArrayBuffer, convert to blob
    if (imageData instanceof ArrayBuffer) {
      return new Blob([imageData], { type: 'image/png' });
    }
    
    throw new Error("Unsupported image data format");
  } catch (error) {
    console.error("Error converting image data to blob:", error);
    throw new Error("Failed to process captured image data");
  }
}

async function insertImageToDocument(imageUrl) {
  try {
    const base64Image = await fetchImageAsBase64(imageUrl);
    
    return new Promise((resolve, reject) => {
      Office.onReady((info) => {
        try {
          switch (info.host) {
            case Office.HostType.Word:
              Word.run(async (context) => {
                const range = context.document.getSelection();
                range.insertInlinePictureFromBase64(base64Image, "Replace");
                await context.sync();
                resolve();
              });
              break;
              
            case Office.HostType.Excel:
              Excel.run(async (context) => {
                const range = context.workbook.getSelectedRange();
                const shape = range.worksheet.shapes.addImage(base64Image);
                shape.width = 300; // Set default width
                shape.height = 300; // Set default height
                await context.sync();
                resolve();
              });
              break;
              
            case Office.HostType.PowerPoint:
              // Use setSelectedDataAsync for PowerPoint
              Office.context.document.setSelectedDataAsync(base64Image, {
                coercionType: Office.CoercionType.Image,
                imageLeft: 100,    // Position from the left in points
                imageTop: 100,     // Position from the top in points
                imageWidth: 300,   // Width in points
                imageHeight: 300   // Height in points
              }, function(asyncResult) {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                  console.log('Image inserted successfully in PowerPoint');
                  resolve();
                } else {
                  console.error('Failed to insert image in PowerPoint:', asyncResult.error.message);
                  reject(new Error(asyncResult.error.message));
                }
              });
              break;
              
            default:
              reject(new Error("Unsupported Office application"));
          }
        } catch (error) {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error("Error inserting image:", error);
    throw error;
  }
}

// Helper function to create a temporary file from base64
async function createTempFileFromBase64(base64Data) {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    // Create a temporary file
    const tempFile = new File([blob], 'temp_image.png', { type: 'image/png' });
    return tempFile;
  } catch (error) {
    console.error("Error creating temporary file:", error);
    throw error;
  }
}

async function fetchImageAsBase64(imageUrl) {
  try {
    console.log("Fetching image from URL:", imageUrl);
    const response = await fetch(imageUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          // Get only the base64 data part
          const base64String = reader.result.split(',')[1];
          // Verify we have valid base64 data
          if (!base64String) {
            reject(new Error("Failed to get valid base64 data from image"));
            return;
          }
          console.log("Image successfully converted to base64");
          resolve(base64String);
        } catch (error) {
          console.error("Error processing base64 data:", error);
          reject(error);
        }
      };
      reader.onerror = (error) => {
        console.error("Error converting image to base64:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error in fetchImageAsBase64:", error);
    throw new Error("Failed to process the generated image: " + error.message);
  }
}

// Update getMoreCredits function to use dialog instead of new tab
async function getMoreCredits() {
  console.log('💳 getMoreCredits function called');
  
  try {
    const userId = authManager.getUserId();
    if (!userId) {
      console.log('❌ No user ID available');
      showError("Authentication required. Please sign in first.");
      return;
    }

    console.log('👤 User ID:', userId);

    // Get initial state (tokens and premium status)
    console.log('🔄 Fetching initial token count and premium status...');
    const [initialTokenResponse, initialPremiumStatus] = await Promise.all([
      fetch(`https://shorts.multiplewords.in/api/tokens_left/get/${userId}`),
      checkPremiumStatus(userId)
    ]);

    if (!initialTokenResponse.ok) {
      throw new Error('Failed to get initial token count');
    }

    const initialData = await initialTokenResponse.json();
    const initialTokens = initialData.credits?.videos || 0;
    
    console.log('📊 Initial state captured:', {
      tokens: initialTokens,
      isPremium: initialPremiumStatus
    });
    
    // Show loader with purchase message and set it as not a generation operation
    console.log('🔄 Showing initial loader');
    showLoader("Processing your purchase...", false);
    
    // Open the pricing page in a dialog instead of a new window
    try {
      console.log('🌐 Opening credits dialog...');
      const result = await authManager.openCreditsDialog(userId);
      console.log("💳 Credits dialog result:", result);
      
      // Don't hide the loader here - keep it showing during the purchase check
      
      // Check if the purchase was successful
      if (result.success) {
        console.log('✅ Dialog indicates success, starting purchase check...');
        // Start polling for changes to token count - this will handle hiding the loader
        startPurchaseCheck(userId, initialTokens, initialPremiumStatus);
      } else {
        console.log('❌ Dialog indicates no purchase or cancellation');
        // Dialog was cancelled or closed without a purchase
        hideLoader();
        showNotification("Purchase cancelled or completed.", "info");
      }
    } catch (error) {
      console.error("❌ Error opening credits dialog:", error);
      showError("Failed to open credits purchase page. Please try again.");
      hideLoader();
      return;
    }
  } catch (error) {
    console.error("❌ Error in getMoreCredits:", error);
    showError("An error occurred while processing your request. Please try again.");
    hideLoader();
  }
}

// Helper function to start checking for purchase completion
function startPurchaseCheck(userId, initialTokens, initialPremiumStatus) {
  console.log('🔄 startPurchaseCheck called with:', {
    userId,
    initialTokens,
    initialPremiumStatus
  });
  
  // Clear any existing interval
  if (purchaseCheckInterval) {
    console.log('🗑️ Clearing existing purchase check interval:', purchaseCheckInterval);
    clearInterval(purchaseCheckInterval);
    purchaseCheckInterval = null;
  }
  
  let hasUpdated = false; // Flag to track if we've already handled the update
  let checkCount = 0; // Counter for debugging
  
  // Ensure loader is showing during the check
  console.log('🔄 Showing loader for purchase check');
  showLoader("Checking for purchase completion...", false);

  // Start polling for changes
  console.log('⏰ Starting purchase check interval (every 3 seconds)');
  purchaseCheckInterval = setInterval(async () => {
    checkCount++;
    console.log(`🔍 Purchase check #${checkCount} triggered, hasUpdated:`, hasUpdated);
    console.log('🔍 Current interval ID:', purchaseCheckInterval);
    
    if (hasUpdated) {
      console.log('⏭️ Skipping check - already updated');
      return; // Skip if we've already handled the update
    }

    try {
      console.log('🌐 Fetching current token count and premium status...');
      
      // Check both token count and premium status
      const [tokenResponse, newPremiumStatus] = await Promise.all([
        fetch(`https://shorts.multiplewords.in/api/tokens_left/get/${userId}`),
        checkPremiumStatus(userId)
      ]);
      
      if (!tokenResponse.ok) {
        console.error('❌ Token response not OK:', tokenResponse.status);
        throw new Error('Failed to get current token count');
      }

      const tokenData = await tokenResponse.json();
      const currentTokens = tokenData.credits?.videos || 0;
      
      console.log('📊 Purchase status check results:', {
        checkNumber: checkCount,
        initialTokens,
        currentTokens,
        initialPremiumStatus,
        newPremiumStatus,
        tokenDiff: currentTokens - initialTokens,
        tokenIncreased: currentTokens > initialTokens,
        becamePremium: (!initialPremiumStatus && newPremiumStatus),
        shouldStop: (currentTokens > initialTokens) || (!initialPremiumStatus && newPremiumStatus)
      });
      
      // Stop checking if either tokens increased or user became premium
      if (currentTokens > initialTokens || (!initialPremiumStatus && newPremiumStatus)) {
        console.log('🎉 Purchase detected! Stopping check and hiding loader');
        console.log('🛑 Setting hasUpdated to true');
        hasUpdated = true; // Set flag to prevent multiple updates
        
        console.log('🗑️ Clearing interval:', purchaseCheckInterval);
        clearInterval(purchaseCheckInterval);
        purchaseCheckInterval = null;
        
        console.log('🔄 Hiding loader');
        hideLoader();
        
        if (newPremiumStatus && !initialPremiumStatus) {
          console.log('✨ User became premium');
          showSuccess("Premium status activated successfully!");
        } else if (currentTokens > initialTokens) {
          console.log('🪙 Tokens increased from', initialTokens, 'to', currentTokens);
          showSuccess(`Credits added successfully! (${currentTokens - initialTokens} tokens added)`);
        }
        
        // Update token display
        console.log('🔄 Updating token display after purchase');
        await checkTokens();
      } else {
        console.log('⏳ No purchase detected yet, continuing to check...');
        console.log('🔍 Will check again in 3 seconds...');
      }
    } catch (error) {
      console.error("❌ Error checking purchase status:", error);
      // Don't stop polling on error, just log it
    }
  }, 3000); // Check every 3 seconds
  
  console.log('✅ Purchase check interval created with ID:', purchaseCheckInterval);
  
  // Set a timeout to stop checking after 5 minutes
  console.log('⏰ Setting 5-minute timeout for purchase check');
  setTimeout(() => {
    console.log('⏰ Purchase check timeout reached');
    if (purchaseCheckInterval && !hasUpdated) {
      console.log('⏰ Timeout: Clearing interval and hiding loader');
      clearInterval(purchaseCheckInterval);
      purchaseCheckInterval = null;
      hideLoader();
      showNotification("Purchase status check timed out. Please refresh if you completed the purchase.", "error");
    } else {
      console.log('⏰ Timeout: But purchase was already detected or interval was cleared');
    }
  }, 5 * 60 * 1000); // 5 minutes timeout
}

// Clean up interval when the page is unloaded
window.addEventListener('unload', () => {
  if (purchaseCheckInterval) {
    clearInterval(purchaseCheckInterval);
    purchaseCheckInterval = null;
  }
});

function showNotification(message, type) {
  const notification = document.getElementById("notification");
  notification.textContent = message;
  notification.className = `notification ${type}`;
  notification.style.display = "block";

  // Hide the notification after 3 seconds
  setTimeout(() => {
    notification.style.display = "none";
  }, 3000);
}

function showError(message) {
  showNotification(message, "error");
}

function showSuccess(message) {
  showNotification(message, "success");
}

// Handle logout
function handleLogout() {
  console.log("Logout button clicked");
  authManager.logout();
  updateAuthUI(false);
  showSuccess("Logged out successfully");
}

// Add function to clear uploaded image
function clearUploadedImage() {
  uploadedFile = null;
  capturedImageData = null;
  isImageEditingMode = false;
  
  const uploadArea = document.getElementById("uploadArea");
  const uploadContent = uploadArea.querySelector(".upload-content");
  const clearButton = document.getElementById("clearUpload");
  
  // Reset upload area to default state
  uploadContent.innerHTML = `
    <div class="upload-icon">📁</div>
    <div class="upload-text">Click to upload an image</div>
  `;
  uploadArea.style.borderColor = "#C8C6C4";
  uploadArea.style.background = "#FAFAFA";
  
  // Hide clear button
  clearButton.style.display = "none";
  
  // Reset file input
  const fileInput = document.getElementById("fileInput");
  fileInput.value = "";
  
  // Update button text to indicate generation mode
  const generateButton = document.getElementById("run");
  const generateButtonLabel = generateButton.querySelector('.ms-Button-label');
  if (!authManager || authManager.isTokenExpired()) {
    generateButtonLabel.textContent = 'Sign in to Generate';
  } else {
    generateButtonLabel.textContent = 'Generate';
  }
  
  showSuccess("Switched back to image generation mode!");
}

// Add function to cancel purchase check
function cancelPurchaseCheck() {
  if (purchaseCheckInterval) {
    clearInterval(purchaseCheckInterval);
    purchaseCheckInterval = null;
    hideLoader();
    showNotification("Purchase check cancelled. Please refresh if you completed the purchase.", "info");
  }
}
